import { ChakraProvider } from "@chakra-ui/react";
import {
  AuthContextProvider,
  CustomCSSReset,
  formatDateForApi,
  LanguageProvider,
  theme,
} from "@questy/aura-cl";
import {
  addDays,
  addMilliseconds,
  differenceInMilliseconds,
  endOfDay,
  startOfDay,
} from "date-fns";
import isEqual from "lodash/isEqual";
import React, { FC, lazy, ReactNode, Suspense } from "react";
import ReactDOM from "react-dom";
import { QueryClientProvider, QueryClient } from "react-query";
import { rrulestr } from "rrule";
import { queryClient } from "../../index";

const WidgetCalendar = lazy(() => import("./widgets/widget-calendar"));
const WidgetCommissions = lazy(() => import("./widgets/widget-commissions"));
const WidgetCommissionCalendar = lazy(
  () => import("./widgets/widget-commission-calendar")
);
const WidgetEmployeesTargets = lazy(
  () => import("./widgets/widget-employees-targets")
);
const WidgetEvents = lazy(() => import("./widgets/widget-events"));
const WidgetFeaturedProducts = lazy(
  () => import("./widgets/widget-featured-products")
);
const WidgetMyTargets = lazy(() => import("./widgets/widget-my-targets"));
const WidgetSlider = lazy(() => import("./widgets/widget-slider"));
const WidgetTasks = lazy(() => import("./widgets/widget-tasks"));
const WidgetTasksForToday = lazy(
  () => import("./widgets/widget-tasks-for-today")
);
const WidgetDocumentOrders = lazy(
  () => import("./widgets/widget-document-orders")
);
const WidgetDocumentOfferQuestionsOH = lazy(
  () => import("./widgets/widget-document-offer-questions-OH")
);
const WidgetDocumentOfferQuestionsKT = lazy(
  () => import("./widgets/widget-document-offer-questions-KT")
);
const WidgetDocumentOfferQuestionsZO = lazy(
  () => import("./widgets/widget-document-offer-questions-ZO")
);
const WidgetDocumentInvoicesPurchase = lazy(
  () => import("./widgets/widget-document-invoices-purchase")
);
const WidgetDocumentInvoicesSale = lazy(
  () => import("./widgets/widget-document-invoices-sale")
);

const WidgetHtml = lazy(() => import("./widgets/widget-html"));

const MockWidget2 = () => {
  return <div>MockWidget2</div>;
};

/**
 * Obiekt mapujący selektor punktu montowania do reactowego komponentu widgetu
 */
export const widgetMountingPointMap: Record<
  string,
  (element: HTMLElement) => JSX.Element
> = {
  "calendar-widget-root": (element: HTMLElement) => (
    <WidgetCalendar el={element} />
  ),
  "widget-html": (element) => <WidgetHtml uuid={element.dataset.uuid!} />,
  "widget-commission-calendar": (element) => (
    <WidgetCommissionCalendar el={element} />
  ),
  "widget-tasks": (element) => <WidgetTasks uuid={element.dataset.uuid!} />,
  "widget-events": (element) => <WidgetEvents uuid={element.dataset.uuid!} />,
  "widget-tasks-for-today": (element) => (
    <WidgetTasksForToday uuid={element.dataset.uuid!} />
  ),
  "widget-mock-2": () => <MockWidget2 />,
  "target-widget": (element) => (
    <WidgetMyTargets widgetId={element.dataset.id!} />
  ),
  "target-users-widget": (element) => (
    <WidgetEmployeesTargets widgetId={element.dataset.id!} />
  ),
  "widget-commissions": (element) => (
    <WidgetCommissions uuid={element.dataset.uuid!} />
  ),
  "widget-slider": (element) => <WidgetSlider uuid={element.dataset.uuid!} />,
  "widget-featured-products": (element) => (
    <WidgetFeaturedProducts uuid={element.dataset.uuid!} />
  ),
  "widget-document-orders": (element) => (
    <WidgetDocumentOrders uuid={element.dataset.uuid!} />
  ),
  "widget-document-offer-questions-OH": (element) => (
    <WidgetDocumentOfferQuestionsOH uuid={element.dataset.uuid!} />
  ),
  "widget-document-offer-questions-KT": (element) => (
    <WidgetDocumentOfferQuestionsKT uuid={element.dataset.uuid!} />
  ),
  "widget-document-offer-questions-ZO": (element) => (
    <WidgetDocumentOfferQuestionsZO uuid={element.dataset.uuid!} />
  ),
  "widget-document-invoices-purchase": (element) => (
    <WidgetDocumentInvoicesPurchase uuid={element.dataset.uuid!} />
  ),
  "widget-document-invoices-sale": (element) => (
    <WidgetDocumentInvoicesSale uuid={element.dataset.uuid!} />
  ),
};

// Na potrzeby testów jednostkowych definiujemy tutaj osobnego QueryClienta
const mockQueryClient = new QueryClient();

const Providers: FC = ({ children }) => (
  <React.StrictMode>
    <Suspense fallback={null}>
      <ChakraProvider theme={theme} resetCSS={false}>
        <CustomCSSReset />
        <QueryClientProvider client={queryClient ?? mockQueryClient}>
          <AuthContextProvider>
            <LanguageProvider>{children}</LanguageProvider>
          </AuthContextProvider>
        </QueryClientProvider>
      </ChakraProvider>
    </Suspense>
  </React.StrictMode>
);

// Kompletny Renderer
export const mountIfPresent = (
  nodes: NodeListOf<HTMLElement>,
  renderFn: (element: HTMLElement) => ReactNode
) => {
  if (nodes?.length > 0) {
    nodes.forEach((element) => {
      if (element.classList.contains("react-mounted")) {
        return;
      } else {
        ReactDOM.render(
          <React.StrictMode>
            <Providers>{renderFn(element)}</Providers>
          </React.StrictMode>,
          element
        );
        element.classList.add("react-mounted");
      }
    });
  }
};

// Montowanie nowo dodanych widgetów
export const mountWidget: MutationCallback = () => {
  const mountingPoints = document.querySelectorAll(
    Object.keys(widgetMountingPointMap)
      .map((mountingPoint) => `.${mountingPoint}`)
      .join(",")
  );

  if (mountingPoints.length < 1) {
    return;
  }

  for (const widgetMountingPoint of Object.entries(widgetMountingPointMap)) {
    mountIfPresent(
      document.querySelectorAll<HTMLElement>(`.${widgetMountingPoint[0]}`),
      widgetMountingPoint[1]
    );
  }
};

/**
 * Funkcja pomocnicza do sprawdzania czy aktualnie pracujemy nad SPA w kontekście
 * Aury - jeżeli tak to można powstrzymać montowanie elementów używanych do
 * standardowego developmentu
 */
export const isInAuraContext = (): boolean => {
  return !!window.jQuery;
};

/*
TASKS
*/

type Task = any;

const DTSTART_EXTRACTOR = new RegExp("(?:^DTSTART.*)", "m");
const RRULE_EXTRACTOR = new RegExp("(?:^RRULE.*)", "m");
const EXDATE_EXTRACTOR = new RegExp("(?:^EXDATE.*)", "gm");

/**
 * Rozszerza listę zadań o pojedyncze wystąpienia zadań cyklicznych w zadanym przedziale czasowym.
 * @param tasks Tablica zadań do przetworzenia
 * @param includePreviousDays - Określa, czy uwzględnić przeterminowane wystąpienia zadań cyklicznych.
 * @param includeNextDaysCount - Liczba dni w przód, dla których należy wygenerować wystąpienia zadań cyklicznych.
 */
export const transformTasks = (
  tasks?: Task[],
  includePreviousDays?: boolean,
  includeNextDaysCount?: number
): Task[] => {
  let newTasks: Task[] = [];

  tasks?.forEach((task) => {
    if (!task.recurrenceActive) {
      newTasks.push(task);
      return;
    }

    try {
      const dtStartString = (task.calendarData as string).match(
        DTSTART_EXTRACTOR
      )?.[0];
      const rruleString = (task.calendarData as string).match(
        RRULE_EXTRACTOR
      )?.[0];
      const exdates = (task.calendarData as string).matchAll(EXDATE_EXTRACTOR);

      const fullRRuleString = `${dtStartString}\n${rruleString}${
        exdates ? `\n${[...exdates].join("\n")}` : ""
      }`;

      if (!rruleString) {
        throw new Error("No RRULE found in recurring task");
      }

      const rrule = rrulestr(fullRRuleString);

      /**
       * Wszystkie wystąpienia tasków cyklicznych od początku czasu lub końca poprzedniego dnia (musi łapać północ).
       * Zachowanie zależne od ustawień widgetu.
       */
      const taskOcurrances = rrule?.between(
        includePreviousDays ? new Date(0) : endOfDay(addDays(new Date(), -1)),
        includeNextDaysCount
          ? endOfDay(addDays(new Date(), includeNextDaysCount))
          : endOfDay(new Date())
      );

      taskOcurrances?.forEach((taskOccurrenceDate) => {
        // TODO: przerobić na nową cykliczność
        // Czy task jest parentem, z którego dzieci są wygenerowane - potrzebne bo tylko ten jeden może mieć id z bazy
        const isParentTask = isEqual(
          taskOccurrenceDate,
          new Date(task.beginDate)
        );

        // Różnica pomiędzy starem i końcem taska, potrzebna do wyliczenia
        // daty końca dla tasków wirtualnych
        let taskDateDifference = null;
        if (task.endDate && task.beginDate) {
          taskDateDifference = differenceInMilliseconds(
            new Date(task?.endDate),
            new Date(task?.beginDate)
          );
        }

        const virtualTask: Task = {
          ...task,
          id: isParentTask ? task.id : null,
          calendarData: null,
          recurrenceParentBeginDate: formatDateForApi(taskOccurrenceDate),
          beginDate: formatDateForApi(taskOccurrenceDate),
          recurrenceActive: isParentTask ? true : false,
          endDate: taskDateDifference
            ? formatDateForApi(
                addMilliseconds(taskOccurrenceDate, taskDateDifference)
              )
            : null,
        };
        newTasks.push(virtualTask);
      });
    } catch (error) {
      console.error(`Task ${task.id} calendarData invalid.`);
    }
  });

  return newTasks;
};

// filtruje i sortuje Wszystkie taski (także cykliczne)
// API nie zwraca wszystkich potomków tasków cyklicznych więc trzeba je wygenerować samemu, żeby pofiltrować i posortować
export const prepareAllTasks = (tasks: any, settings: any, limit: number) => {
  let result = tasks;

  // data
  let beginDate: any = {};

  if (settings?.beginDate?.max > -1) {
    let day = new Date();
    if (settings.beginDate.maxType?.id === 2) {
      day.setDate(day.getDate() + parseInt(settings.beginDate.max));
      beginDate["to"] = endOfDay(day);
    } else {
      day.setDate(day.getDate() - parseInt(settings.beginDate.max));
      beginDate["from"] = startOfDay(day);
    }
  } else {
    if (settings?.beginDate?.from) {
      beginDate["from"] = startOfDay(new Date(settings?.beginDate?.from));
    }
    if (settings?.beginDate?.to) {
      beginDate["to"] = endOfDay(new Date(settings?.beginDate?.to));
    }
  }

  if (beginDate.from) {
    result = result.filter(
      (task: any) => new Date(task.beginDate) > new Date(beginDate.from)
    );
  }
  if (beginDate.to) {
    result = result.filter(
      (task: any) => new Date(task.beginDate) < new Date(beginDate.to)
    );
  }

  // sortowanie
  if (Array.isArray(settings?.sorting) && settings.sorting.length > 0) {
    settings.sorting.reverse().forEach((element: any) => {
      if (element?.value) {
        const sortField = element.value.split("_");

        result = result.sort((a: any, b: any) => {
          return sortField[1] === "ASC"
            ? prepareSortFields(sortField[0], a[sortField[0]]) -
                prepareSortFields(sortField[0], b[sortField[0]])
            : prepareSortFields(sortField[0], b[sortField[0]]) -
                prepareSortFields(sortField[0], a[sortField[0]]);
        });
      }
    });
  }

  // limit

  return result.slice(0, limit);
};

const prepareSortFields = (fieldName: string, fieldValue: any) => {
  if (["beginDate"].includes(fieldName)) {
    return new Date(fieldValue);
  }

  return fieldValue;
};
